import {useCallback, useEffect, useRef, useState} from 'react';
import type {RefCallback} from 'react';

interface UseInfiniteScrollOptions {
  onLoadMore: () => Promise<void> | void;
  hasNextPage: boolean;
  // Required: omitting it silently defaults to "not loading", which lets the
  // sentinel fire again while a page is already in flight.
  isLoading: boolean;
  rootMargin?: string;
  threshold?: number;
  enabled?: boolean;
  root?: Element | Document | null;
}

export function useInfiniteScroll({
  onLoadMore,
  hasNextPage,
  isLoading,
  rootMargin = '0px 0px 300px 0px',
  threshold = 0,
  enabled = true,
  root,
}: UseInfiniteScrollOptions) {
  const [node, setNode] = useState<Element | null>(null);
  const observerRoot = root ?? null;
  const [intersection, setIntersection] = useState<{
    node: Element;
    root: Element | Document | null;
    isIntersecting: boolean;
  } | null>(null);
  const onLoadMoreRef = useRef(onLoadMore);

  useEffect(() => {
    onLoadMoreRef.current = onLoadMore;
  }, [onLoadMore]);

  const loadMoreTriggerRef = useCallback<RefCallback<Element>>(
    (nextNode) => setNode(nextNode),
    [],
  );

  useEffect(() => {
    if (!node) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIntersection({
          node: entry.target,
          root: observerRoot,
          isIntersecting: entry.isIntersecting,
        });
      },
      {
        root: observerRoot,
        rootMargin,
        threshold,
      },
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, [node, observerRoot, rootMargin, threshold]);

  useEffect(() => {
    const isCurrentIntersection =
      intersection?.node === node && intersection.root === observerRoot;

    if (
      isCurrentIntersection &&
      intersection.isIntersecting &&
      enabled &&
      hasNextPage &&
      !isLoading
    ) {
      void onLoadMoreRef.current();
    }
  }, [node, observerRoot, intersection, enabled, hasNextPage, isLoading]);

  return loadMoreTriggerRef;
}
